import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "374",
  "slug": "gall-peters-projection-explained",
  "title": "What Is the Gall-Peters Projection? The Map That Started a Fight",
  "excerpt": "The Gall-Peters projection shows every country at its true relative size — and in doing so it sparked one of the longest-running arguments in cartography.",
  "date": "2026-06-08",
  "category": "Geography",
  "image": "https://images.unsplash.com/photo-1524661135-423995f22d0b?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHx2aW50YWdlJTIwd29ybGQlMjBtYXB8ZW58MXwwfHx8MTc4MDkwNjI4Nnww&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "4 min",
  "author": "EarthGuessr Team",
  "tldr": "The Gall-Peters projection is an equal-area world map: every country appears at its correct relative size. It became famous in the 1970s as a political counterpoint to the Mercator map, which exaggerates the size of wealthy northern countries.",
  "content": [
    {
      "type": "paragraph",
      "text": "Every flat map of a round planet is a compromise. You can keep shapes accurate, or sizes accurate, or directions accurate — but never all three at once. The map you grew up with almost certainly chose accurate shapes and directions at the expense of size. The Gall-Peters projection makes the opposite trade, and that choice turned it into one of the most argued-about maps in history."
    },
    {
      "type": "heading",
      "text": "What Makes It Different"
    },
    {
      "type": "paragraph",
      "text": "Gall-Peters is an 'equal-area' projection. That means any two regions that cover the same number of square kilometres on the ground cover the same number of square centimetres on the map. Africa looks enormous — because it is. Greenland shrinks down to its true, modest size. Europe is no longer puffed up at the top of the page."
    },
    {
      "type": "paragraph",
      "text": "The trade-off is shape. To keep areas honest, the projection stretches landmasses vertically near the equator and squashes them near the poles, giving countries an oddly elongated look. Africa and South America appear taller and narrower than they feel on a globe."
    },
    {
      "type": "heading",
      "text": "Where the Name Comes From"
    },
    {
      "type": "paragraph",
      "text": "The projection itself was first described by the Scottish clergyman James Gall in 1855, where it sat largely ignored for over a century. It was revived in the 1970s by the German filmmaker and historian Arno Peters, who presented it as a fairer alternative to the standard Mercator map. Because both men are credited, it's usually called the Gall-Peters projection."
    },
    {
      "type": "paragraph",
      "text": "Peters wasn't really making a cartographic argument — he was making a political one."
    },
    {
      "type": "heading",
      "text": "Why It Caused an Argument"
    },
    {
      "type": "paragraph",
      "text": "The Mercator projection, designed for sea navigation in the 1500s, dramatically inflates the size of countries far from the equator. On a Mercator map, Greenland looks roughly as big as Africa, even though Africa is about fourteen times larger. Europe and North America loom large; Africa, South America, and South Asia look smaller than they really are."
    },
    {
      "type": "paragraph",
      "text": "Peters argued that this wasn't a harmless quirk. A map that consistently shrinks the developing world and enlarges the wealthy north, he said, quietly shapes how people picture global importance. Promoting an equal-area map was, for him, a matter of fairness."
    },
    {
      "type": "heading",
      "text": "The Cartographers' Pushback"
    },
    {
      "type": "paragraph",
      "text": "Professional mapmakers were not impressed by the way Peters framed his case. They pointed out that equal-area projections were nothing new, that the Gall-Peters shapes were badly distorted, and that there were many better equal-area options available. In the late 1980s several major geographic bodies even passed resolutions discouraging the use of rectangular world maps altogether — both Mercator and Gall-Peters."
    },
    {
      "type": "paragraph",
      "text": "Still, the controversy did its job. It pushed the size-distortion problem of Mercator into public view, and equal-area maps like the Equal Earth projection are now widely used in atlases and the news."
    },
    {
      "type": "heading",
      "text": "The Moment It Hit Pop Culture"
    },
    {
      "type": "paragraph",
      "text": "The debate jumped from geography journals into living rooms thanks to a memorable scene in the TV drama The West Wing, where a fictional group called 'Cartographers for Social Equality' lobbies White House staff to adopt the Gall-Peters map, arguing the familiar Mercator distorts our mental picture of the world. It's a neat dramatisation of a real argument — and for many viewers it was the first time they'd ever questioned the map on the wall."
    },
    {
      "type": "heading",
      "text": "The Compromise Maps"
    },
    {
      "type": "paragraph",
      "text": "Most modern atlases avoid both extremes. Instead of choosing perfect shape or perfect area, they use compromise projections that spread the distortion around so nothing looks too wrong. The Robinson and Winkel Tripel projections are popular choices for exactly this reason — National Geographic has used both for its reference world maps — and the newer Equal Earth projection keeps areas honest while looking far more natural than Gall-Peters."
    },
    {
      "type": "heading",
      "text": "Which Map Should You Use?"
    },
    {
      "type": "paragraph",
      "text": "There's no single 'correct' world map — only maps suited to different jobs. For navigation, Mercator's straight compass lines are genuinely useful. For comparing the size of countries or showing global data fairly, an equal-area map wins. The lesson of the Gall-Peters fight is simply to know what your map is exaggerating before you trust it."
    }
  ]
};
